import { createSelector } from '@reduxjs/toolkit';
import { ProductTypeEnum } from '@talos/kyoko';
import type { AppState } from '../../../../providers/AppStateProvider/types';
import { getOTCRepoQuantityCurrencyOptions } from '../currencies';
import type { OTCRepoFormState } from './otcRepoTypes';

export type OTCRepoCollateralAssetOption = {
  type: 'currency' | 'bond';
  value: string;
  label: string;
};

const selectCurrenciesBySymbol = (state: AppState) => state.rfq.referenceData.currencies.currenciesBySymbol;
const selectSecuritiesList = (state: AppState) => state.rfq.referenceData.securities.securitiesList;

const selectIsUsingOTCRepos = (state: AppState) => state.rfq.subProductType === 'OTCRepo';

const selectCashCurrencyOptions = createSelector(selectCurrenciesBySymbol, currenciesBySymbol => [
  ...currenciesBySymbol.keys(),
]);
const selectCollateralAssetOptions = createSelector(
  selectCurrenciesBySymbol,
  selectSecuritiesList,
  (currenciesBySymbol, securitiesList): OTCRepoCollateralAssetOption[] => {
    const currencyOptions = [...currenciesBySymbol.keys()].map(currency => ({
      type: 'currency' as const,
      value: currency,
      label: currency,
    }));
    const bondOptions = securitiesList
      .filter(security => security.ProductType === ProductTypeEnum.GovernmentBond)
      .map(security => ({
        type: 'bond' as const,
        value: security.Symbol,
        label: security.DisplaySymbol ?? security.Symbol,
      }));

    return [...currencyOptions, ...bondOptions];
  }
);

const selectHasAssetSelection = createSelector(
  (state: AppState) => state.rfq.otcRepoForm.cashCurrencyField,
  (state: AppState) => state.rfq.otcRepoForm.collateralAssetField,
  (cashCurrencyField, collateralAssetField) => {
    return cashCurrencyField.hasValue && collateralAssetField.hasValue;
  }
);

export function isOTCRepoFormValid(otcRepoForm: OTCRepoFormState): boolean {
  return (
    !otcRepoForm.cashCurrencyField.hasInvalidValue &&
    !otcRepoForm.collateralAssetField.hasInvalidValue &&
    !otcRepoForm.startTimeField.hasInvalidValue &&
    !otcRepoForm.endTimeField.hasInvalidValue
  );
}

const selectQuantityCurrencyOptions = createSelector(
  (state: AppState) => state.rfq.otcRepoForm.cashCurrencyField.value,
  (state: AppState) => state.rfq.otcRepoForm.collateralAssetField.value,
  selectHasAssetSelection,
  (cashCurrency, collateralAsset, hasAssetSelection) =>
    hasAssetSelection ? getOTCRepoQuantityCurrencyOptions({ cashCurrency, collateralAsset }) : undefined
);

const selectSelectionMarkets = createSelector(selectIsUsingOTCRepos, isUsingOTCRepos => {
  if (!isUsingOTCRepos) {
    return undefined;
  }

  // hard-coded for dev purposes, backend needs to add a flag to Market to let us do anything else
  return ['marketplace_etf_lp1', 'marketplace_etf_lp2'];
});

export const otcRepoSelectors = {
  selectCashCurrencyOptions,
  selectCollateralAssetOptions,
  selectHasAssetSelection,
  isOTCRepoFormValid,
  selectQuantityCurrencyOptions,
  selectSelectionMarkets,
};
